import { useEffect } from "react";

export const SITE_NAME = "SHUWA";
export const DEFAULT_TITLE = "SHUWA — Book salons, barbers, spas & clinics in Rwanda";
export const DEFAULT_DESCRIPTION =
  "Find and book hair salons, barbers, nail studios, massage, clinics and more near you in Rwanda. Compare prices and reviews, pick a time, and book in seconds.";
const DEFAULT_IMAGE = "/icon-512.png";

// What each public page tells search engines. Shop pages and search results
// describe themselves from their data (see useSeo in those pages).
const PAGES = {
  "/": { title: DEFAULT_TITLE, description: DEFAULT_DESCRIPTION },
  "/search": {
    title: "Find and book salons, barbers and more near you | SHUWA",
    description: "Search businesses on SHUWA by service, name or city. See prices, opening hours and reviews, then book online.",
  },
  "/appointment": {
    title: "Book an appointment near you | SHUWA",
    description: "Browse businesses near you, compare reviews and book an appointment online in a few taps.",
    canonical: "/search",
  },
  "/for-business": {
    title: "SHUWA for business — online booking, calendar and payments",
    description: "Get booked online around the clock. SHUWA gives your salon, barbershop, spa or clinic a booking page, calendar, reminders and payments in one app.",
  },
  "/for-business/pricing": {
    title: "Pricing — SHUWA for business",
    description: "Simple pricing for SHUWA: start free, and upgrade to Pro when your team grows. See plans, fees and what's included.",
  },
  "/for-business/payout": {
    title: "Payouts — how and when your business gets paid | SHUWA",
    description: "How online payments on SHUWA reach your business: your SHUWA wallet, mobile money withdrawals and fees.",
  },
  "/download": {
    title: "Download the SHUWA app for iPhone and Android",
    description: "Book and manage your appointments on the go. Get the SHUWA app on the App Store and Google Play.",
  },
  "/about": {
    title: "About SHUWA — your appointment, assured",
    description: "SHUWA connects customers in Rwanda with trusted salons, barbers, spas, clinics and other local businesses — and makes booking them simple.",
  },
  "/contact": {
    title: "Contact SHUWA — we're here to help",
    description: "Questions about a booking, a payment or listing your business? Get in touch with the SHUWA team.",
  },
  "/legal/terms": { title: "Terms of Service | SHUWA", description: "The terms that apply when you use SHUWA to book or to offer appointments." },
  "/legal/privacy": { title: "Privacy Policy | SHUWA", description: "What personal data SHUWA collects, why, and the choices you have." },
  "/legal/security": { title: "Security | SHUWA", description: "How SHUWA protects your account, your data and your payments." },
  "/legal/business-guidelines": { title: "Key Guidelines for Businesses | SHUWA", description: "The essentials for businesses taking bookings on SHUWA: payouts, cancellations, availability and booking fees." },
};

// Signed-in areas and sign-in forms: nothing for a search engine to list.
const PRIVATE = /^\/(account|business|admin|login|register|forgot-password|delete-account)(\/|$)|\/book$/;

export function pageSeo(pathname) {
  const path = pathname.replace(/\/+$/, "") || "/";

  if (PRIVATE.test(path)) return { noindex: true };

  return PAGES[path] || {};
}

function setMeta(attribute, key, content) {
  let tag = document.head.querySelector(`meta[${attribute}="${key}"]`);

  if (!content) {
    tag?.remove();
    return;
  }

  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attribute, key);
    document.head.appendChild(tag);
  }

  tag.setAttribute("content", content);
}

const absolute = (url) => new URL(url, window.location.origin).href;

/**
 * Writes a page's title, description, canonical URL, social-preview tags
 * and structured data into <head>. `canonical` is a path (query included
 * when it changes what the page shows); `jsonLd` is a schema.org object.
 * `lang` is the language the page is written in, and `alternates` the same
 * page in each language it exists in: [{ lang, path }].
 */
export function applySeo({ title, description, canonical, image, noindex = false, jsonLd, lang = "en", alternates = [] } = {}) {
  const fullTitle = title || DEFAULT_TITLE;
  const fullDescription = description || DEFAULT_DESCRIPTION;
  const url = absolute(canonical || window.location.pathname);
  const imageUrl = absolute(image || DEFAULT_IMAGE);

  document.title = fullTitle;
  setMeta("name", "description", fullDescription);
  setMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow");
  setMeta("property", "og:title", fullTitle);
  setMeta("property", "og:description", fullDescription);
  setMeta("property", "og:url", url);
  setMeta("property", "og:image", imageUrl);
  setMeta("name", "twitter:title", fullTitle);
  setMeta("name", "twitter:description", fullDescription);
  setMeta("name", "twitter:image", imageUrl);

  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", url);

  document.documentElement.lang = lang;
  document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((tag) => tag.remove());
  alternates.forEach((alternate) => {
    const tag = document.createElement("link");
    tag.setAttribute("rel", "alternate");
    tag.setAttribute("hreflang", alternate.lang);
    tag.setAttribute("href", absolute(alternate.path));
    document.head.appendChild(tag);
  });

  // The page's own structured data; the site-wide block in index.html stays.
  document.getElementById("page-jsonld")?.remove();
  if (jsonLd) {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "page-jsonld";
    script.textContent = JSON.stringify(jsonLd);
    document.head.appendChild(script);
  }
}

/**
 * For pages whose title and description come from their data. Pass null
 * while the data is loading to keep what the route set.
 */
export function useSeo(seo) {
  const key = seo ? JSON.stringify(seo) : null;

  useEffect(() => {
    if (key) applySeo(JSON.parse(key));
  }, [key]);
}

const SCHEMA_TYPES = {
  "hair-salons": "HairSalon",
  barbers: "HairSalon",
  "nail-studios": "NailSalon",
  "makeup-artists": "BeautySalon",
  massage: "DaySpa",
  clinics: "MedicalClinic",
  "health-clinics": "MedicalClinic",
  dentists: "Dentist",
  physiotherapy: "MedicalBusiness",
  "car-wash": "AutoWash",
  mechanics: "AutoRepair",
  "tattoo-studios": "TattooParlor",
  "home-cleaners": "HouseCleaning",
};

const SCHEMA_DAYS = { mon: "Monday", tue: "Tuesday", wed: "Wednesday", thu: "Thursday", fri: "Friday", sat: "Saturday", sun: "Sunday" };

/** schema.org description of a shop, so search results can show its rating, address and hours. */
export function businessJsonLd(business, image) {
  const hours = Object.entries(business.opening_hours || {}).flatMap(([day, slots]) =>
    SCHEMA_DAYS[day] && Array.isArray(slots)
      ? slots
          .filter((slot) => slot?.start && slot?.end)
          .map((slot) => ({ "@type": "OpeningHoursSpecification", dayOfWeek: SCHEMA_DAYS[day], opens: slot.start, closes: slot.end }))
      : []
  );

  return {
    "@context": "https://schema.org",
    "@type": SCHEMA_TYPES[business.category?.slug] || "LocalBusiness",
    name: business.name,
    url: absolute(`/${business.slug}`),
    ...(image && { image: absolute(image) }),
    ...(business.description && { description: business.description }),
    ...(business.phone && { telephone: business.phone }),
    ...((business.address || business.city) && {
      address: {
        "@type": "PostalAddress",
        ...(business.address && { streetAddress: business.address }),
        ...(business.city && { addressLocality: business.city }),
        addressCountry: "RW",
      },
    }),
    ...(business.latitude && business.longitude && {
      geo: { "@type": "GeoCoordinates", latitude: Number(business.latitude), longitude: Number(business.longitude) },
    }),
    ...(hours.length > 0 && { openingHoursSpecification: hours }),
    ...(business.rating_count > 0 && {
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: Number(business.rating_avg).toFixed(1),
        reviewCount: business.rating_count,
      },
    }),
    potentialAction: { "@type": "ReserveAction", target: absolute(`/${business.slug}/book`) },
  };
}
